import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { SortKey, SortOrder } from "../../utils/documentSorting";
import { DateHint } from "../atoms/DateHint";

interface DocumentListHeaderProps {
  /** Callback when select all checkbox is toggled */
  onSelectAll: (isSelected: boolean) => void;
  /** All documents are selected */
  isAllSelected: boolean;
  /** Some documents are selected (indeterminate state) */
  isIndeterminate: boolean;
  /** Show/hide Checkbox column */
  showCheckbox?: boolean;
  /** Show/hide Version column */
  showVersion?: boolean;
  /** Show/hide Date Created column */
  showDateCreated?: boolean;
  /** Show/hide Status column */
  showStatus?: boolean;
  /** Retained for caller compatibility — the user now renders inline in each row, not as a header column. */
  showUser?: boolean;
  /** Show/hide Tags column */
  showTags?: boolean;
  /** Show/hide Parent column */
  showParent?: boolean;
  /** Show/hide Actions column */
  showActions?: boolean;
  /** Current sort key */
  sortKey?: SortKey;
  /** Current sort order */
  sortOrder?: SortOrder;
  /** Callback when a sortable column header is clicked */
  onSort?: (key: SortKey) => void;
  /** Additional CSS classes */
  className?: string;
  /** Optional content rendered at the right edge of the header (e.g. pagination) */
  endContent?: React.ReactNode;
}

/**
 * DocumentListHeader - Organism component
 *
 * Table header row with column labels and select-all checkbox.
 * Defines the structure and layout for the document list table.
 */
export function DocumentListHeader({
  onSelectAll,
  isAllSelected,
  isIndeterminate,
  showCheckbox = true,
  showVersion = true,
  showDateCreated = true,
  showStatus = true,
  showTags = true,
  showParent = false,
  showActions = true,
  sortKey,
  sortOrder,
  onSort,
  className,
  endContent,
}: DocumentListHeaderProps) {
  const { t } = useLanguage();
  // Build grid columns dynamically based on visibility
  const gridColumns = [
    showCheckbox && "28px", // Checkbox
    "minmax(var(--document-name-min-width, 240px), 2.5fr)", // Name (always shown; the table lowers its floor via --document-name-min-width when even the fixed columns barely fit) — the dominant flexible column; filenames are the primary content, so they get ~2.5× the tags column's share
    showDateCreated && "140px", // File modified — wide enough to keep the label + always-visible sort arrow on one row (fits the longer Estonian "Faili muutmisaeg")
    showVersion && "80px", // Version
    showStatus && "100px", // Status
    showTags && "minmax(180px, 1fr)", // Tags
    showParent && "150px", // Parent
    showActions && "50px", // Actions
  ]
    .filter(Boolean)
    .join(" ");

  // Shared column-label style — matches the Issues page header (the table-design
  // source of truth): uppercase, tracking-wide, muted, `text-[10px]`.
  // Applied per-label rather than on the parent grid so the inline Search
  // input doesn't inherit the uppercase transform.
  const colLabel = "text-[10px] font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    // Plain (no filled background) header with a single thin bottom border,
    // matching the Issues page table.
    <div className={cn("relative border-b px-6 py-1.5", className)}>
      <div
        className="grid items-center gap-4"
        style={{
          gridTemplateColumns: gridColumns,
        }}
      >
        {/* Select All Checkbox */}
        {showCheckbox && (
          <div className="flex items-center justify-center">
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onCheckedChange={(checked) => onSelectAll(checked === true)}
              aria-label={t.documents.table.selectAll}
            />
          </div>
        )}

        {/* Column Labels */}
        <div className="flex items-center gap-2">
          <span
            className={cn(colLabel, onSort && "cursor-pointer select-none")}
            onClick={onSort ? () => onSort("name") : undefined}
          >
            {t.documents.table.name}
            {onSort && <SortIcon field="name" activeField={sortKey} direction={sortOrder} />}
          </span>
        </div>
        {showDateCreated && (
          <div
            className={cn(
              colLabel,
              "whitespace-nowrap text-center",
              onSort && "cursor-pointer select-none"
            )}
            onClick={onSort ? () => onSort("modified_at") : undefined}
          >
            <DateHint hint={t.documents.table.fileModifiedColumnTooltip}>
              {t.documents.table.dateModified}
            </DateHint>
            {onSort && <SortIcon field="modified_at" activeField={sortKey} direction={sortOrder} />}
          </div>
        )}
        {showVersion && (
          <div className={cn(colLabel, "text-center")}>{t.documents.table.version}</div>
        )}
        {showStatus && (
          <div className={cn(colLabel, "text-center")}>{t.documents.table.status}</div>
        )}
        {showTags && <div className={colLabel}>{t.documents.table.tags}</div>}
        {showParent && <div className={colLabel}>{t.documents.table.parent}</div>}
        {showActions && (
          <div className={cn(colLabel, "opacity-0")} aria-hidden="true">
            {t.documents.table.actions}
          </div>
        )}
      </div>

      {endContent && (
        <div className="mt-1 flex justify-end xl:absolute xl:right-6 xl:top-1/2 xl:z-10 xl:mt-0 xl:-translate-y-1/2 xl:pl-4">
          {endContent}
        </div>
      )}
    </div>
  );
}

// Sort affordance: every sortable column shows a permanently-visible indicator.
// The active column shows the directional up/down arrow; inactive sortable columns
// show a muted neutral up/down icon so the sort handle is discoverable at all times
// (per reporter feedback — not hover-only, not active-column-only). Size matches the
// standardized active arrow (`h-3 w-3`).
function SortIcon({
  field,
  activeField,
  direction,
}: {
  field: SortKey;
  activeField?: SortKey;
  direction?: SortOrder;
}) {
  if (field !== activeField) {
    return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted-foreground/50" />;
  }
  return direction === "asc" ? (
    <ArrowUp className="ml-1 inline h-3 w-3" />
  ) : (
    <ArrowDown className="ml-1 inline h-3 w-3" />
  );
}
